import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
  DestroyRef,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Shop, unbookableType } from '../../../shared/entities/shop';
import { PlatformInformationService } from '../../../../../projects/platform-manager/src/lib/services/platform-information.service';
import { combineLatest, filter } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ShopService, eventTimeType } from '../../../shared/services/shop.service';
import { LocalizeRouterModule, LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Meta, Title } from '@angular/platform-browser';
import { TownService } from '../../../core/services/town.service';
import { TagManagerService } from '../../../core/services/tag-manager.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { CommonModule, DatePipe } from '@angular/common';
import { NavigationService } from '../../../core/services/navigation.service';
import { ResolutionService } from '../../../core/services/resolution.service';
import { ResponseService } from '../../../core/services/response.service';
import { Town } from '../../../shared/entities/town';
import { BROWSER_WINDOW } from '../../../core/injection-tokens/browser-window';
import { PartnerLinkService } from '../../../core/services/partner-link.service';
import { ShopDetailService } from '../../../shared/services/shop-detail.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { SHOPLIST_ANIMATIONS } from '../../../core/animations/SHOPLIST_ANIMATIONS';
import {
  BookingParametersInterface,
  codetype,
  copyBookingParameters
} from '../../../shared/entities/bookingParameters';
import { ShopSortingService } from '../../../shared/services/shop-sorting.service';
import { BookingParameterService } from '../../../core/services/booking-parameter.service';
import { ShopSearchComponent } from '../../../shared/landing-page/search/shop-search/shop-search.component';
import { ScrollService } from '../../../core/services/scroll.service';
import { ShopSearchHelperService } from '../../../shared/landing-page/search/shop-search-helper.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TownNotFoundInterface } from '../../../shared/interfaces/town-not-found-interface';
import { ShopListInterface } from '../../../shared/interfaces/shop-list-interface';
import { SkihirePoiInterface } from '../../../shared/interfaces/skihire-poi-interface';
import { DeferLoadModule } from '@trademe/ng-defer-load';
import { IconComponent, alarm, arrowLeft, phone, provideIcons, review } from '../../../shared/icons';
import { SkeletonLoaderComponent } from '../../../shared/components/skeleton-loader/skeleton-loader.component';
import { ShopFilterComponent } from '../../../shared/saleslocation/filter/shop-filter.component';
import { ShopComponent } from '../../../shared/saleslocation/shop/shop.component';
import { ShopSortComponent } from '../../../shared/saleslocation/shop-sort/shop-sort.component';
import { TownFurtherInfoComponent } from '../../../shared/shop-town/components/town-further-info/town-further-info.component';
import { ShopListAdvantagesComponent } from '../shop-list-advantages/shop-list-advantages.component';
import { TrustpilotComponent } from '../../../shared/components/trustpilot/trustpilot.component';
import { PaymentMethodsComponent } from '@shared/components';
import { DisclaimersComponent } from '../../../shared/shop-town/components/disclaimers/disclaimers.component';
import { SpecialSalesService } from '../../../core/services/special-sales.service';
import { ShopFilterService } from '../../../core/services/shop-filter.service';
import { SelectedHotelService } from '../../../shared/services/selected-hotel.service';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { MapService } from '../../../shared/services/map.service';
import { CrowdinTranslationService } from '../../../shared/services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';
import { StructuredDataService } from '../../../shared/services/structured-data.service';
import { MicrositeHelperService } from '../../../core/services/microsite-helper.service';

@Component({
  selector: 'app-shop-list',
  templateUrl: './shop-list.component.html',
  styleUrls: ['./shop-list.component.scss'],
  animations: SHOPLIST_ANIMATIONS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideIcons({
      alarm,
      arrowLeft,
      phone,
      review
    })
  ],
  imports: [
    CommonModule,
    RouterModule,
    LocalizeRouterModule,
    AlpitranslatePipe,
    DeferLoadModule,
    IconComponent,
    DisclaimersComponent,
    // Lazy loaded
    ShopFilterComponent,
    ShopSortComponent,
    ShopComponent,
    SkeletonLoaderComponent,
    TownFurtherInfoComponent,
    ShopListAdvantagesComponent,
    TrustpilotComponent,
    PaymentMethodsComponent,
    ShopSearchComponent
  ]
})
export class ShopListComponent implements OnInit {
  // the current displayed bookingParameters info. will be updated by events from the search form
  protected bookingParameters: BookingParametersInterface;
  // the town loaded with the townService
  protected town: Town;
  // display loading indicator or not. Loading for shops
  protected loading = false;
  // false on init, will be set to true if apiService got an error response.
  protected error = false;
  // date until alpinflexi is offered for free!
  protected freeFlexiUntil: Date;
  protected showFreeFlexi = false;
  // the current culture
  protected culture = this.translateService.currentLang;
  protected isBrowser = this.platformInformationService.getIsBrowser();
  // used to show/hide content based on viewport
  protected showAdditionalElements = false;
  protected displayDesktopElements = false;
  // determines how many shops to lazyLoad in or skip lazy loading after sorting or townchange
  protected numberOfInitialShow = 0;
  protected skipDeferLoading: boolean;

  protected saleslocationNotFound: TownNotFoundInterface;
  protected saleslocationEmpty = false; // used when shops are loaded but api was empty (no shops in saleslocation)

  protected hotlines: { [key: string]: string };
  protected hotline: string;
  // the shops loaded with the apiService (see updateShops subscription)
  protected shops: Array<Shop>;

  protected showGroupDiscountFootnote = false; // on init, this returns always false: this.getShowGroupDiscoutFootNote();
  protected showTownFurtherInformation = false;
  protected teaserHeadline =
    this.specialSalesService.getActiveSale()?.townPageTitleTranskey ?? 'noTeaserHeadline';

  // if the redirect contains invalidDate query infos
  private invalidDate = false;
  // if the redirect contains invalidAge query infos
  private invalidAge = false;
  // the invalid shop id from the redirect query
  private invalidShopId: number;
  private countryCode: string;
  platformName: string;

  @ViewChild('search', { static: true }) search: ShopSearchComponent;
  @ViewChild('shopListAdvantage', { read: ViewContainerRef }) protected shopListAdvantage: ViewContainerRef;
  @ViewChild('rating', { read: ViewContainerRef }) protected rating: ViewContainerRef;
  @ViewChild('paymentMethods', { read: ViewContainerRef }) protected paymentMethods: ViewContainerRef;

  constructor(
    protected shopFilterService: ShopFilterService,
    private mapService: MapService,
    private route: ActivatedRoute,
    protected translateService: TranslateService,
    protected platformInformationService: PlatformInformationService,
    private partnerLinkService: PartnerLinkService,
    private shopService: ShopService,
    private townService: TownService,
    private localizedRouterService: LocalizeRouterService,
    private router: Router,
    private navigationService: NavigationService,
    private meta: Meta,
    private title: Title,
    private tagManager: TagManagerService,
    private notificationService: NotificationService,
    private datePipe: DatePipe,
    private resolutionService: ResolutionService,
    private responseService: ResponseService,
    @Inject(BROWSER_WINDOW) private window,
    private shopDetailService: ShopDetailService,
    private analyticsService: AnalyticsService,
    protected shopSortingService: ShopSortingService,
    private bookingParameterService: BookingParameterService,
    private scrollService: ScrollService,
    protected shopSearchHelperService: ShopSearchHelperService,
    private specialSalesService: SpecialSalesService,
    private selectedHotelService: SelectedHotelService,
    private crowdinService: CrowdinTranslationService,
    private structuredDataService: StructuredDataService,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private micrositeHelperService: MicrositeHelperService
  ) {
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    if (this.isBrowser) {
      this.numberOfInitialShow =
        this.resolutionService.getCurrentSizeReference() > this.resolutionService.extraSmallReference ? 2 : 0;
      this.showAdditionalElements =
        this.resolutionService.getCurrentSizeReference() > this.resolutionService.extraSmallReference;
      this.displayDesktopElements =
        this.resolutionService.getCurrentSizeReference() > this.resolutionService.mediumReference;
    }
    const domainName = this.platformInformationService.getPlatformName().substring(4).toLowerCase();
    this.platformName = domainName.charAt(0).toUpperCase() + domainName.slice(1).toLowerCase();
  }

  private injectCanonicalLink() {
    const params = this.route.snapshot.paramMap;
    this.micrositeHelperService.injectCanonicalLink(
      this.localizedRouterService.translateRoute(
        `/SKIHIREENTRY/${params.get('country')}/${params.get('province')}/${params.get('town')}`
      ) as string
    );
  }

  ngOnInit() {
    this.crowdinService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => {
        this.updateMetaDescriptionText();
        this.updateTitle();
        this.cdr.markForCheck();
      }
    });

    this.showTownFurtherInformation = this.platformInformationService.getCurrentPlatformShortcode() === 'ar';
    // shopservice has already loaded shops? use them
    this.shops = this.shopService.currentShops;
    this.loading = this.shops.length ? false : true;

    // set up bookingparams
    this.bookingParameters = this.bookingParameterService.getBookingParameters();
    if (!this.bookingParameters.ages || !this.bookingParameters.ages.length) {
      this.bookingParameters.ages = [36];
    }

    this.selectedHotelService.onHotelChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sortShops(this.shopSortingService.getCurrentSelection());
    });

    this.resolutionService.sizeReferenceChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(currentSizeReference => {
        this.showAdditionalElements = currentSizeReference > this.resolutionService.extraSmallReference;
        this.displayDesktopElements = currentSizeReference > this.resolutionService.mediumReference;
        this.cdr.detectChanges();
      });

    this.shopDetailService.showOnMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.openMapForShop.bind(this));
    this.shopDetailService.displayingShop
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onDisplayingShop());
    this.shopDetailService.goToProduct
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onGoToProduct.bind(this));

    this.townService.salesLocationNotFound
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onSaleslocationNotFoundUpdate.bind(this));
    this.townService.onTownUpdate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((town: Town) => {
      this.town = town;
      const paramBag = {};
      Object.keys(town.townSlugs).forEach(key => {
        const split = town.townSlugs[key].split('/');
        paramBag[key] = {
          ':country': split[0],
          ':province': split[1],
          ':town': split[2]
        };
      });
      this.structuredDataService.setAlternateLangLinks(this.route.snapshot.pathFromRoot, paramBag);
      this.updateTitle();
      this.cdr.markForCheck();
    });

    this.localizedRouterService.routerEvents
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onLanguageChange.bind(this));

    // errors on shop loading
    this.shopService.loadingError
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onErrorUpdate.bind(this));

    this.shopService.updates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      switch (event.type) {
        case eventTimeType.preShopUpdate:
          this.preShopUpdate(event.data.bookingParameters);
          break;
        case eventTimeType.onShopUpdate:
          this.onShopUpdate(event.data);
          break;
        case eventTimeType.onInTown:
          this.onOfferUpdate(event.data);
          break;
        case eventTimeType.onOutOfTown:
          this.onOutOfTownOfferUpdate(event.data);
      }
      this.cdr.detectChanges();
    });

    this.platformInformationService.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.freeFlexiUntil = new Date(data.freeFlexiUntil);
      this.freeFlexiUntil.setDate(this.freeFlexiUntil.getDate() - 1);
      this.showFreeFlexi = new Date(data.freeFlexiUntil) >= new Date();
      this.hotlines = data.hotlines;
      this.hotline = this.hotlines[this.translateService.currentLang];
    });

    // will be 'AT' on SSR, see getCustomerCountryCode() in platformservice
    this.platformInformationService.customerCountryCode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customerCountryCode: string) => {
        this.countryCode = customerCountryCode;
        if (this.countryCode === 'FR') {
          this.hotline = this.hotline.replace('+33 ', '0');
        }
      });

    this.mapService.shopChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((shop: Shop) => {
      this.mapService.closeMap();
      // TODO: we need to reset the selected filters so the customer will be scrolled to the correct shop
      this.scrollToSelectedShop(shop);
    });

    combineLatest([
      this.shopService.updates.pipe(filter(event => event.type === eventTimeType.onShopUpdate)),
      this.townService.onTownUpdate
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(paramBag => {
        this.updateMetaDescriptionText();
        if (
          paramBag[0].data.shops[0] &&
          this.platformInformationService.getCurrentPlatformShortcode() === 'ar'
        ) {
          this.setStructuredData(paramBag[1], paramBag[0].data.shops);
        }
      });

    // TODO: this should be changed to a route data entry
    // got rerouted from product page because shop is not available!
    const queryParams = this.route.snapshot.queryParamMap;
    if (queryParams.has('invalidDates') && queryParams.has('shopId')) {
      this.invalidDate = true;
      this.invalidShopId = +queryParams.get('shopId');
      this.router.navigate([], {
        queryParams: { invalidDates: null, shopId: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
    // got rerouted from product page because shop is not available!
    if (queryParams.get('invalidAges') && queryParams.get('shopId')) {
      this.invalidAge = true;
      this.invalidShopId = +queryParams.get('shopId');
      this.router.navigate([], {
        queryParams: { invalidAges: null, shopId: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    // get townslug and prepare all services that will use it
    const params = this.route.snapshot.paramMap;
    if (params.has('country') && params.has('province') && params.has('town')) {
      // set the townslug according to current url
      this.bookingParameters.slug =
        params.get('country') + '/' + params.get('province') + '/' + params.get('town');
      // "fake" the poi we're currently on to enable correct <- landing page -> town page switch without search page
      this.bookingParameters.poi = {
        type: 'AlpinTown',
        url: '/de/skiverleih/' + this.bookingParameters.slug
      };
      // prepare the shop service to load shops and discount ticker
      this.ngZone.runOutsideAngular(() => {
        this.shopService.updateBookingParameters(this.bookingParameters);
        this.shopSearchHelperService.updateBookingParameters(this.bookingParameters);
      });

      // update metadescription when town and shop info is available (needs both, be cautious of race condition!)
      this.townService.getTownForSlug(this.bookingParameters.slug);
    }
    this.injectCanonicalLink();
    this.townService.getTownForSlug(this.bookingParameters.slug);
  }

  private setStructuredData(town: Town, shops: ShopListInterface[]) {
    // add breadcrumbs on 'how we got here'
    const url = this.localizedRouterService.translateRoute('/SKIHIREENTRY');
    this.structuredDataService.setBreadcrumbList([
      {
        name: town.countryNames[this.translateService.currentLang],
        link: `${url.toString()}/${town.countrySlugs[this.translateService.currentLang]}`
      },
      {
        name: town.provinceNames[this.translateService.currentLang],
        link: `${url.toString()}/${town.provinceSlugs[this.translateService.currentLang]}`
      },
      {
        name: town.name,
        link: `${url.toString()}/${town.townSlugs[this.translateService.currentLang]}`
      }
    ]);

    // add links to all the shops you can go to
    const productSelectionUrl = this.localizedRouterService.translateRoute('/SKIHIREENTRY/PRODUCTS');
    this.structuredDataService.setSportingGoodsStoreItemList({
      name: this.translateService.instant('SHOPLIST.title', {
        town_name: town.name,
        country_name: town.countryNames[this.translateService.currentLang]
      }),
      elements: shops.map(shop => {
        return {
          url: productSelectionUrl + '?shopId=' + shop.id,
          shop: shop
        };
      })
    });
  }

  /**
   * intersector runs outside angular, let the shop view update
   * @param shop the shop that's about to become visible
   */
  showShop(shop: Shop) {
    shop.lazyshow = true;
    this.cdr.detectChanges();
  }

  /**
   * Scrolls the shop in the list selected on the map.
   * @param shop
   */
  private scrollToSelectedShop(shop: ShopListInterface) {
    setTimeout(() => {
      this.scrollService.scrollIntoView(document.getElementById('scroll-shop-' + shop.id));
    }, 100);
  }

  protected retryLoad() {
    this.error = false;
    this.shopService.updateBookingParameters(this.bookingParameters, true);
  }

  /**
   * sorts shops according to given sorting type (price, distance, price_distance)
   * @param type sorting type
   * @param displayRoedler if the sort loading indicator should be displayed or not
   */
  protected sortShops(type: string) {
    this.shops = this.shopSortingService.sortShops(type, this.shops);
    return this.shops;
  }

  /**
   * helps angular to keep track of shops for changeDetection and animations. See:
   * https://angular.io/api/core/TrackByFunction
   * @param index loop index
   * @param shop shop that is currently looped
   * @returns
   */
  protected shopTrackedBy(index: number, shop: ShopListInterface) {
    return shop.id + shop.distance;
  }

  private onSaleslocationNotFoundUpdate(townNotFound: TownNotFoundInterface) {
    this.responseService.setResponseCode(404);
    this.error = true;
    this.loading = false;
    this.saleslocationNotFound = townNotFound;
  }

  private onDisplayingShop() {
    return (shop: ShopListInterface) => {
      this.scrollToSelectedShop(shop);
    };
  }

  private getShowGroupDiscoutFootNote(): boolean {
    if (this.shops) {
      for (const shop of this.shops) {
        if (!Array.isArray(shop.discountPromotions)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * get infos for language changes, update services and texts that will not change automatically
   * @param lang string of the selected language (for example 'en' or 'de')
   */
  private onLanguageChange(lang: string) {
    this.culture = lang;

    if (this.isBrowser) {
      const slug = this.town.getSlugByCulture(this.culture);
      const url = this.localizedRouterService.translateRoute('/SKIHIREENTRY/' + slug);
      this.router.navigate([url], { queryParamsHandling: 'merge', replaceUrl: true });
    }
  }

  private onGoToProduct(shop: Shop) {
    if (this.bookingParameters.userDefinedSearch) {
      // send user to product selection
      const route = this.localizedRouterService.translateRoute('/SKIHIREENTRY/SKIHIRE.PRODUCTS');
      const routeWithPartner = this.partnerLinkService.getUrlReplacingPartnerAfterPrefix(
        route,
        this.localizedRouterService.translateRoute('/SKIHIREENTRY')
      );
      try {
        this.window.history.scrollRestoration = 'auto';
      } catch {
        /* empty */
      }
      const queryParams = { shopId: shop.id };

      this.window.location.href = this.router
        .createUrlTree([routeWithPartner], {
          queryParams: queryParams
        })
        .toString();
    } else {
      // send user back to form
      this.shopSearchHelperService.lastSelectedShop = shop;
      this.goToSearch();
    }
  }

  protected goToSearch() {
    this.search.setFocus({ behavior: 'smooth' });
  }

  /**
   * set page title (the string in your browser tab)
   */
  private updateTitle() {
    if (this.town) {
      this.translateService
        .get('SHOPLIST.title', {
          town_name: this.town.seoNames[this.translateService.currentLang],
          country_name: this.town.countryNames[this.translateService.currentLang]
        })
        .subscribe((res: string) => {
          this.title.setTitle(res);
        });
    }
  }

  /**
   * set the meta tag for SEO purposes
   */
  private updateMetaDescriptionText() {
    // set meta description
    if (this.shops && this.town) {
      this.structuredDataService.setMetaTags(
        [
          {
            name: 'description',
            content: 'SHOPLIST.metaDescription'
          },
          {
            name: 'og:description',
            content: 'SHOPLIST.openGraphDescription'
          },
          {
            name: 'og:title',
            content: 'SHOPLIST.openGraphTitle'
          }
        ],
        {
          country_name: this.town.countryNames[this.translateService.currentLang],
          shop_count: this.shops.length,
          discount: this.town.metaDescriptionDiscount,
          town_name: this.town.name
        }
      );
    }
  }

  private openMapForShop(shop: ShopListInterface) {
    if (shop) {
      this.router.navigate([{ outlets: { modal: ['map', shop.id] } }], {
        relativeTo: this.route
      });
    } else {
      this.router.navigate([{ outlets: { modal: ['map'] } }], {
        relativeTo: this.route
      });
    }
  }

  private createAnalyticsEvent(eventCategory: string, eventAction: string) {
    this.analyticsService.sendData('send', {
      hitType: 'event',
      eventCategory: eventCategory,
      eventAction: eventAction,
      eventLabel: ''
    });
  }

  // #region ShopService Events
  /**
   * this takes care about telling people why a shop can not be booked
   * // TODO: this is one very long, very ugly function. It would be great if we can let a route data entry and a
   * resolver take care about that
   */
  private onUnbookableUpdate() {
    if (this.invalidDate || this.invalidAge) {
      this.invalidDate = false;
      this.invalidAge = false;
      const invalidShop = this.shops.find((predicate: Shop) => {
        if (+predicate.id === this.invalidShopId) {
          return true;
        }
      });
      const unbookableReasons = invalidShop?.unbookableReasonsFor(
        this.bookingParameters.start,
        this.bookingParameters.end,
        this.bookingParameters.ages
      );
      if (invalidShop && unbookableReasons?.length) {
        let transkey = '';
        switch (unbookableReasons[0].type) {
          case unbookableType.minDuration:
            transkey = 'NOTIFICATION.shopMinDuration';
            this.createAnalyticsEvent(
              'Error Message',
              `The minimum booking duration in this store is ${unbookableReasons[0].minDuration} day(s).`
            );
            break;
          case unbookableType.maxDuration:
            transkey = 'NOTIFICATION.shopMaxDuration';
            this.createAnalyticsEvent(
              'Error Message',
              `The maximum booking duration in this store is ${unbookableReasons[0].minDuration} days.`
            );
            break;
          case unbookableType.minDelay:
            transkey = 'NOTIFICATION.shopMinDelay';
            this.createAnalyticsEvent(
              'Error Message',
              'We are sorry, This shop can not accept any bookings at short notice.'
            );
            break;
          case unbookableType.after:
            transkey = 'NOTIFICATION.shopAfterSeason';
            this.createAnalyticsEvent(
              'Error Message',
              `The shop will be closed after ${unbookableReasons[0].end}. Please choose another period`
            );
            break;
          case unbookableType.before:
            transkey = 'NOTIFICATION.shopBeforeSeason';
            this.createAnalyticsEvent(
              'Error Message',
              `The shop is only open from the ${unbookableReasons[0].start}. Please choose another period`
            );
            break;
          case unbookableType.pause:
            transkey = 'NOTIFICATION.shopPause';
            this.createAnalyticsEvent(
              'Error Message',
              `Out of stock for the period ${unbookableReasons[0].start} - ${unbookableReasons[0].end}. Please choose another period`
            );
            break;
          case unbookableType.unservableAges:
            transkey = 'NOTIFICATION.shopUnservableAges';
            break;
        }

        this.translateService.get('CORE.notificationDateFormat').subscribe(dateFormat => {
          this.notificationService.add({
            displayTime: 0,
            transkey: transkey,
            transvars: {
              shop_name: invalidShop.name,
              shop_address: invalidShop.address,
              start_date: this.datePipe.transform(unbookableReasons[0].start, dateFormat, undefined, 'en-US'),
              end_date: this.datePipe.transform(unbookableReasons[0].end, dateFormat, undefined, 'en-US'),
              age: unbookableReasons[0].age,
              min_delay: unbookableReasons[0].minDelay,
              max_duration: unbookableReasons[0].maxDuration,
              min_duration: unbookableReasons[0].minDuration
            },
            id: 'blocker'
          });
        });
      }
    }
  }

  /**
   * something broke during shop api fetches
   */
  private onErrorUpdate() {
    this.loading = false;
    this.error = true;
    this.cdr.detectChanges();
  }

  /**
   * Shops will begin to update
   * @param bookingParameters
   */
  private preShopUpdate(bookingParameters: BookingParametersInterface) {
    this.bookingParameters = bookingParameters;
    this.loading = true;
    this.saleslocationEmpty = false;
    this.error = false;
    this.saleslocationNotFound = null;
  }

  /**
   * Shops have been updated (no offers yet)
   * @param response
   */
  private onShopUpdate(response: { bookingParameters: BookingParametersInterface; shops: Array<Shop> }) {
    this.selectedHotelService.setHotel(null);
    this.loading = false;
    if (!response.shops.length) {
      // no shops in saleslocation
      this.error = true;
      this.saleslocationEmpty = true;
      this.responseService.setResponseCode(404);
    }
    this.bookingParameters = response.bookingParameters;
    this.shops = response.shops;
    this.onUnbookableUpdate();
    this.showGroupDiscountFootnote = this.getShowGroupDiscoutFootNote();
  }

  /**
   * "In Town" offers now mapped to shops
   * @param response
   */
  private onOfferUpdate(response: { bookingParameters: BookingParametersInterface; shops: Array<Shop> }) {
    this.bookingParameters = response.bookingParameters;
    this.shops = response.shops;
    // when sorting by price, we need to wait until out of town offers are ready
    if (this.shopSortingService.getCurrentSelection() !== 'price') {
      this.loading = false;
      this.sortShops(this.shopSortingService.getCurrentSelection());
      this.tagManager.push({ event: 'optimize.activate' });
    }
  }

  /**
   * "Out of Town" offers now mapped to shops
   * @param response
   */
  private onOutOfTownOfferUpdate(response: {
    shops: Array<Shop>;
    bookingParameters: BookingParametersInterface;
  }) {
    // loaded offers for out of town shops.
    if (response.shops.length > 0) {
      this.bookingParameters = response.bookingParameters;
      this.shops = response.shops;
      this.loading = false;
      this.sortShops(this.shopSortingService.getCurrentSelection());
    }
    if (this.shopSortingService.getCurrentSelection() === 'price') {
      this.tagManager.push({ event: 'optimize.activate' });
    }
  }
  // #endregion
  // #region SearchComponent Outlets
  private supportInteractiveMode(): boolean {
    return this.bookingParameters.userDefinedSearch && this.resolutionService.isLargeDevice();
  }

  protected updateLocation(event: { slug: string; poi: SkihirePoiInterface }) {
    // changing the location might cause nearby shops that have already been loaded to be set to lazyshow false
    // since the deferLoad subscription has been fulfilled and unsubscribed, render the shop with this flag.
    // TODO: also use it on filters
    this.skipDeferLoading = true;
    if (this.supportInteractiveMode()) {
      this.townService.getTownForSlug(event.slug);
      let reload = false;
      if (this.saleslocationNotFound) {
        this.saleslocationNotFound = null;
        reload = true;
        this.error = false;
      }
      const data = copyBookingParameters(this.bookingParameters);
      data.slug = event.slug;
      data.poi = event.poi;
      this.shopService.updateBookingParameters(data, reload);
      if (data.partner) {
        this.navigationService.navigateToPoi(event.poi, false, data.partner.replace('partner-', 'partner-/'));
      } else {
        this.navigationService.navigateToPoi(event.poi);
      }
    } else if (event.poi.type === 'AlpinShop') {
      if (this.bookingParameters.partner) {
        this.navigationService.navigateToPoi(
          event.poi,
          false,
          this.bookingParameters.partner.replace('partner-', 'partner-/')
        );
      } else {
        this.navigationService.navigateToPoi(event.poi);
      }
    }
  }

  /**
   * updates offers with given array<number> of ages
   * @param ages
   */
  protected updatePersons(ages: Array<number>) {
    if (this.supportInteractiveMode()) {
      const data = copyBookingParameters(this.bookingParameters);
      data.ages = ages;
      this.shopService.updateBookingParameters(data);
    }
  }

  /**
   * updates offers with given promocode
   * @param code new valid promocode
   */
  protected updatePromocode(code: string) {
    if (this.supportInteractiveMode()) {
      const data = copyBookingParameters(this.bookingParameters);
      data.setActioncode({ type: codetype.promo, text: code });
      this.shopService.updateBookingParameters(data);
    }
  }

  /**
   * update offers and unbookable info depending on date range
   * @param range Array of start and end date
   */
  protected updateTimerange(range: { start: Date; end: Date }) {
    if (this.supportInteractiveMode()) {
      const data = copyBookingParameters(this.bookingParameters);
      data.start = range.start;
      data.end = range.end;
      this.shopService.updateBookingParameters(data);
    }
  }

  protected updateForm(event: BookingParametersInterface, forceReload?: boolean) {
    // switched town, we cant scroll anymore so remove last selected shop
    if (event.slug.localeCompare(this.bookingParameters.slug) !== 0) {
      this.shopSearchHelperService.lastSelectedShop = null;
    }
    this.bookingParameters.userDefinedSearch = event.userDefinedSearch;
    this.shopService.updateBookingParameters(event, forceReload);
    this.townService.getTownForSlug(event.slug);
    if (this.shopSearchHelperService.lastSelectedShop) {
      this.scrollToSelectedShop(this.shopSearchHelperService.lastSelectedShop);
      this.shopSearchHelperService.lastSelectedShop = null;
    }

    if (event.poi) {
      if (this.bookingParameters.partner) {
        this.navigationService.navigateToPoi(
          event.poi,
          false,
          this.bookingParameters.partner.replace('partner-', 'partner-/')
        );
      } else {
        this.navigationService.navigateToPoi(event.poi);
      }
    }
  }
  // #endregion
}
